<template>
  <v-breadcrumbs
    bg-color="color_c"
    density="compact"
    tile
    :class="breadcrumbClass"
    :divider="breadcrumbDivider"
    :items="breadcrumbsItems"
  />
</template>

<script setup>
import {
  computed, onMounted, ref, watch,
} from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import useUserDashboards from '~/composables/useUserDashboards';
import useNavigation from '~/composables/navigation/useNavigation';
import useDashboard from '~/composables/useDashboard';
import useDynamicBreadcrumb from '~/composables/useDynamicBreadcrumb';

const { ensureAllDashboards } = useUserDashboards();
const {
  selectedMachine,
  groupSelected,
  groupsSelected,
  selectedFactory,
  showGroups,
} = useNavigation();
const { selectedDashboard } = useDashboard();
const { dynamicTitle } = useDynamicBreadcrumb();
const route = useRoute();
const { t } = useI18n();

onMounted(async () => {
  await ensureAllDashboards();
});

const oldSelectedFactory = ref({});
const oldSelectedMachine = ref({});

const breadcrumbDivider = computed(() => '›');
const breadcrumbClass = computed(() => 'text-color_g text-no-wrap text-truncate');

const matched = computed(() => route?.matched);

/**
 * Checks if a name is an i18n key and translates it if necessary.
 */
function checkNameIsi18nKey(name) {
  // Added check to ensure name is actually a string before calling includes
  if (name !== undefined && typeof name === 'string' && name.includes('navigation.')) {
    return t(`${name}`);
  }
  return name;
}

/**
 * Check if machine object exist in group array
 */
function machineExistInGroup(group, machineId) {
  // Added check for group existence as well
  if (!group?.machines || !machineId) {
    return false;
  }
  // Ensure machines array exists before calling some
  // eslint-disable-next-line max-len
  return Array.isArray(group.machines) && group.machines.some((machine) => machine?.machine_id === machineId);
}

/**
 * Check if push machine or not
 * @returns {boolean}
 */
function addMachineOnBreadcrumbsItems() {
  const currentFactoryId = selectedFactory?.value?.factory_id;
  const previousFactoryId = oldSelectedFactory?.value?.factory_id;
  const currentMachineId = selectedMachine?.value?.machine_id;
  const previousMachineId = oldSelectedMachine?.value?.machine_id;
  const currentGroup = groupSelected?.value;

  if (currentFactoryId !== previousFactoryId
      && machineExistInGroup(currentGroup, currentMachineId)) {
    return true;
  }
  if ((machineExistInGroup(currentGroup, previousMachineId)
       || machineExistInGroup(currentGroup, currentMachineId))
      && currentFactoryId === previousFactoryId) {
    return true;
  }
  return false;
}

/**
 * Build breadcrumbs for the settings/production page
 * @returns {*}
 */
function getBreadcrumbsItemsSettingsPage() {
  if (!matched.value) return [];
  const items = matched.value.flatMap((match) => {
    const { breadcrumb } = match.meta;
    if (Array.isArray(breadcrumb)) {
      return breadcrumb.map((item) => ({
        title: t(item),
        disabled: false,
        link: false,
      }));
    }

    if (typeof breadcrumb === 'string') {
      return [{
        title: t(breadcrumb),
        disabled: false,
        link: false,
      }];
    }

    return [];
  });

  if (dynamicTitle.value) {
    items.push({ title: dynamicTitle.value, disabled: false, link: false });
  }

  // Special-case: for '/profile/preferences' remove the 'Profil' segment from parent
  if (route?.name === 'myProfile.preferences' || route?.name === 'myProfile.access') {
    return items.filter((it) => it.title !== t('user.profile'));
  }

  return items;
}

function getFirstTitle(dashboard) {
  if (dashboard.categoryExpand.dc_name === 'Table') {
    return t('breadcrumb.table');
  }

  return t('dashboard.dashboards');
}

/**
 * Build breadcrumbs for the dashboards page
 * @returns {*}
 */
function getBreadcrumbsItemsDashboardPage() {
  let items = [];
  // Add guard clause for selectedDashboard
  if (!selectedDashboard.value) return [];

  items = [{
    title: getFirstTitle(selectedDashboard.value),
    disabled: false,
    link: false,
  }, {
    title: selectedDashboard.value.title,
    disabled: false,
    link: false,
  }, {
    title: checkNameIsi18nKey(selectedFactory.value?.factory_name),
    disabled: false,
    link: false,
  }];

  // If the dashboard is a table, remove the last item
  if (selectedDashboard.value.categoryExpand.dc_name === 'Table') {
    return items.splice(0, 2);
  }

  // Skip group and machine if it's the 'Abnormal Vibrations' dashboard
  if (selectedDashboard.value.id === '1014') return items;
  // Add group info if a group is selected.
  if (groupSelected.value || showGroups.value) {
    let title = checkNameIsi18nKey(groupSelected.value?.group_name);

    // Skip group info for GlobalKPI and GlobalLosses dashboards
    if (['1011', '1012'].includes(selectedDashboard.value.id)) {
      // If there are multiple groups, show the base title and the count of additional groups
      const groupCount = groupsSelected.value.length;
      title = groupCount > 1 ? `${title} +${groupCount - 1}` : title;
    }

    items.push({
      title,
      disabled: false,
      link: false,
    });
  }

  // If the dashboard's category is "Machine" and the condition to add a machine is met,
  // add machine info.
  if (selectedDashboard.value?.categoryExpand?.dc_name === 'Machine' && addMachineOnBreadcrumbsItems()) {
    items.push({
      title: selectedMachine.value?.machine_name,
      disabled: false,
      link: false,
    });
  }

  return items;
}

/**
 * Build breadcrumbs for the events page
 * @returns {*}
 */
function getBreadcrumbsItemsEventsPage() {
  let tempItems = [];
  tempItems = [{
    title: t('dashboard.dashboards'),
    disabled: false,
    link: false,
  }, {
    title: t('navigation.drawer.dashboard.label.events'),
    disabled: false,
    link: false,
  }, {
    title: checkNameIsi18nKey(selectedFactory.value?.factory_name),
    disabled: false,
    link: false,
  }];

  if (groupSelected.value) {
    tempItems.push({
      title: checkNameIsi18nKey(groupSelected.value?.group_name),
      disabled: false,
      link: false,
    });
  }
  if (addMachineOnBreadcrumbsItems()) {
    tempItems.push({
      title: selectedMachine.value?.machine_name,
      disabled: false,
      link: false,
    });
  }
  return tempItems;
}

/**
 * Build breadcrumbs for the categorization page
 * @returns {*}
 */
function getBreadcrumbsItemsCategorizationPage() {
  let tempItems = [];
  tempItems = [{
    title: t('dashboard.dashboards'),
    disabled: false,
    link: false,
  }, {
    title: t('menu.primary.event.categorization'),
    disabled: false,
    link: false,
  }, {
    title: checkNameIsi18nKey(selectedFactory.value?.factory_name),
    disabled: false,
    link: false,
  }];

  if (groupSelected.value) {
    tempItems.push({
      title: checkNameIsi18nKey(groupSelected.value?.group_name),
      disabled: false,
      link: false,
    });
  }
  if (addMachineOnBreadcrumbsItems()) {
    tempItems.push({
      title: selectedMachine.value?.machine_name,
      disabled: false,
      link: false,
    });
  }
  return tempItems;
}

/**
 * Build breadcrumbs for the planning pages
 * @returns {*}
 */
function getBreadcrumbsItemsPlanningPage() {
  const items = [{
    title: t('planning.planning'),
    disabled: false,
    link: false,
  }];

  if (route?.meta?.breadcrumb) {
    items.push({
      title: t(route.meta.breadcrumb),
      disabled: false,
      link: false,
    });
  }

  return items;
}

/**
 * method computed use to return v-breadcrumbs items
 */
function getBreadcrumbsItems(currentRoute) {
  let tempsItems = [];
  if (currentRoute?.name === 'dashboardId' && selectedDashboard.value) {
    tempsItems = tempsItems.concat(getBreadcrumbsItemsDashboardPage());
  } else if (currentRoute?.name === 'categorization') {
    tempsItems = tempsItems.concat(getBreadcrumbsItemsCategorizationPage());
  } else if (currentRoute?.name === 'event') {
    tempsItems = tempsItems.concat(getBreadcrumbsItemsEventsPage());
  } else if ((currentRoute?.name) && (currentRoute.name.includes('planning')) && (!currentRoute.name.includes('planning_options'))) {
    tempsItems = tempsItems.concat(getBreadcrumbsItemsPlanningPage());
  } else {
    tempsItems = tempsItems.concat(getBreadcrumbsItemsSettingsPage());
  }

  return tempsItems;
}

/**
 * Compute breadcrumbs items based on current route and state
 * Adds active styling to last item
 * BEWARE : HACK FOR 1015 AND 1016 : 2 items
 */
const breadcrumbsItems = computed(() => {
  if (!matched.value) return [];

  let items = getBreadcrumbsItems(route).filter((item) => item.title !== '');

  const isLimitedDashboard = route.name === 'dashboardId'
    && ['1015', '1016'].includes(route.params?.value);

  const maxItems = isLimitedDashboard ? 2 : items.length;
  items = items.slice(0, maxItems);

  if (items.length) {
    Object.assign(items[items.length - 1], {
      active: true,
      activeClass: 'text-white font-weight-medium',
    });
  }

  return items;
});

watch(selectedFactory, (val, oldVal) => {
  oldSelectedFactory.value = oldVal;
});

watch(selectedMachine, (val, oldVal) => {
  oldSelectedMachine.value = oldVal;
});

</script>
